import { UserDetails } from '@coach-bot/data-access/shared/models';
import { AbstractStudentStore } from '@coach-bot/data-access/shared/stores/abstract/abstract-student.store';
import { isRestrictedEmail } from '@coach-bot/data-access/shared/utils/permissions';
import { EXTENSION_ICONS } from '@shared/extension/constants';
import { getActiveTab } from '@shared/extension/utils/tabs';
import { ONE_MINUTE_MS } from '@shared/utils/constants';
import { BehaviorSubject, Subject, Subscription, combineLatest, distinctUntilChanged, skip, timer } from 'rxjs';
import {
  DEFAULT_WATCH_LAST_LEARNING_ACTIVITY_TIME,
  DEFAULT_WATCH_RECOMMENDATIONS_INTERVAL_MS,
  DEFAULT_WATCH_STUDENT_BALANCE_INTERVAL_MS,
  DEFAULT_WATCH_THIRD_PARTY_APP_CREDENTIALS_INTERVAL_MS,
} from '../constants';
import { StudentState } from '../states';
import { LearningService } from './learning.service';

export class StudentWatchService {
  readonly extensionIcon$ = new BehaviorSubject<string>(EXTENSION_ICONS.LEARN_WITH_AI.DEFAULT);
  readonly showLearningReminder$ = new Subject<void>();

  private isLearningAndSignedInSubscription: Subscription | null = null;
  private loadAndCheckLastLearningActivitySubscription: Subscription | null = null;
  private loadLastLearningActivityTimeSubscription: Subscription | null = null;
  private loadRecommendationsSubscription: Subscription | null = null;
  private loadStudentBalanceSubscription: Subscription | null = null;
  private loadThirdPartyAppCredentialsListSubscription: Subscription | null = null;
  private signedInSubscription: Subscription | null = null;
  private watchRecommendationsIntervalMsSubscription: Subscription | null = null;
  private watchStudentBalanceIntervalMsSubscription: Subscription | null = null;

  private watching: boolean = false;

  constructor(
    private isExtensionPopupOpened$: BehaviorSubject<boolean>,
    private isSignedId$: BehaviorSubject<boolean>,
    private learningService: LearningService,
    private profileEmail$: BehaviorSubject<string>,
    private studentState: StudentState,
    private studentStore: AbstractStudentStore,
    private userDetails$: BehaviorSubject<UserDetails | null>
  ) {}

  /**
   * The `startWatching` function sets up event listeners and subscriptions to watch for changes in the
   * user's sign-in status and intervals, and starts watching recommendations and student balance if
   * the user is signed in.
   */
  startWatching(): void {
    if (this.watching) {
      return;
    }
    this.watching = true;

    this.studentStore.setWatchRecommendationsIntervalMs(DEFAULT_WATCH_RECOMMENDATIONS_INTERVAL_MS);
    this.studentStore.setWatchStudentBalanceIntervalMs(DEFAULT_WATCH_STUDENT_BALANCE_INTERVAL_MS);
    this.signedInSubscription = this.isSignedId$
      .pipe(skip(1), distinctUntilChanged())
      .subscribe(async (isSignedId: Boolean) => {
        if (isSignedId) {
          this.watchLastLearningActivityTime();
          this.watchRecommendations();
          this.watchStudentBalance();
          this.watchThirdPartyAppCredentialsList();
          if (!this.studentStore.lastLearningActivityTime$.value) {
            // Use timer to prevent displaying notification immediately after sign-in
            this.loadAndCheckLastLearningActivitySubscription = timer(ONE_MINUTE_MS).subscribe(() => {
              this.loadAndCheckLastLearningActivitySubscription = null;
              this.loadAndCheckLastLearningActivity();
            });
          }
        } else {
          this.stopWatchingLastLearningActivityTime();
          this.stopWatchingRecommendations();
          this.stopWatchingStudentBalance();
          this.stopWatchingThirdPartyAppCredentialsList();
          this.studentStore.setLastLearningActivityTime(null);
          this.studentStore.setLearningReminderDisplayTimes(null);
          this.studentStore.setMoneyReward(null);
          this.studentStore.setRecommendations(null);
          this.studentStore.setWaitingForRewardRecommendation(null);
          this.studentStore.setStudent(null);
          this.studentStore.setStudentBalance(null);
          this.studentStore.setThirdPartyAppCredentialsList(null);
          if (this.loadAndCheckLastLearningActivitySubscription) {
            this.loadAndCheckLastLearningActivitySubscription.unsubscribe();
            this.loadAndCheckLastLearningActivitySubscription = null;
          }
        }
      });

    this.watchRecommendationsIntervalMsSubscription = this.studentStore.watchRecommendationsIntervalMs$.subscribe(
      () => {
        if (this.isSignedId$.value) {
          this.stopWatchingRecommendations();
          this.watchRecommendations();
        }
      }
    );

    this.watchStudentBalanceIntervalMsSubscription = this.studentStore.watchStudentBalanceIntervalMs$.subscribe(
      async () => {
        if (this.isSignedId$.value) {
          this.stopWatchingStudentBalance();
          this.watchStudentBalance();
        }
      }
    );

    this.isLearningAndSignedInSubscription = combineLatest([
      this.studentStore.isLearningAnyRecommendation$,
      this.isSignedId$,
      this.profileEmail$,
    ]).subscribe(
      async ([isLearningAnyRecommendation, isSignedId, profileEmail]: [boolean, boolean, string]) => {
        const isUserRestricted: boolean = isRestrictedEmail(profileEmail);
        let extensionIcon: string;
        const icons = isUserRestricted ? EXTENSION_ICONS.LEARN_WITH_AI : EXTENSION_ICONS.LEARN_AND_EARN;
        if (isSignedId && !isUserRestricted) {
          if (isLearningAnyRecommendation) {
            extensionIcon = icons.LEARNING;
          } else {
            extensionIcon = icons.IDLE;
          }
        } else {
          extensionIcon = icons.DEFAULT;
        }
        if (this.extensionIcon$.value !== extensionIcon) {
          this.extensionIcon$.next(extensionIcon);
        }
      }
    );

    chrome.tabs.onUpdated.addListener(this.handleTabUpdated.bind(this));
    chrome.tabs.onActivated.addListener(this.updateCurrentUrl.bind(this));

    this.updateCurrentUrl();
  }

  stopWatching(): void {
    if (!this.watching) {
      return;
    }
    this.stopWatchingRecommendations();
    this.stopWatchingStudentBalance();
    this.isLearningAndSignedInSubscription.unsubscribe();
    this.isLearningAndSignedInSubscription = null;
    this.signedInSubscription.unsubscribe();
    this.signedInSubscription = null;
    this.watchRecommendationsIntervalMsSubscription.unsubscribe();
    this.watchRecommendationsIntervalMsSubscription = null;
    this.watchStudentBalanceIntervalMsSubscription.unsubscribe();
    this.watchStudentBalanceIntervalMsSubscription = null;
    chrome.tabs.onUpdated.removeListener(this.handleTabUpdated);
    chrome.tabs.onActivated.removeListener(this.updateCurrentUrl);

    this.watching = false;
  }

  async updateCurrentUrl(): Promise<void> {
    const activeTab: chrome.tabs.Tab | null = await getActiveTab();
    this.studentStore.setCurrentUrl(activeTab?.url || null);
  }

  /**
   * Recommendations
   */

  private watchRecommendations(): void {
    if (this.loadRecommendationsSubscription) {
      return;
    }
    this.subscribeToLoadingRecommendations();
  }

  private stopWatchingRecommendations(): void {
    if (this.loadRecommendationsSubscription) {
      this.loadRecommendationsSubscription.unsubscribe();
      this.loadRecommendationsSubscription = null;
    }
  }

  private async subscribeToLoadingRecommendations(): Promise<void> {
    // For now, use interval to update student recommendations from time to time until event from backend is implemented
    const intervalMs: number =
      this.studentStore.watchRecommendationsIntervalMs$.value || DEFAULT_WATCH_RECOMMENDATIONS_INTERVAL_MS;
    this.loadRecommendationsSubscription = timer(intervalMs).subscribe(this.handleLoadingRecommendations.bind(this));
  }

  private async handleLoadingRecommendations(): Promise<void> {
    await this.studentState.loadRecommendations();
    if (this.loadRecommendationsSubscription !== null) {
      this.subscribeToLoadingRecommendations();
    }
  }

  /**
   * Student Balance
   */

  private watchStudentBalance(): void {
    if (this.loadStudentBalanceSubscription) {
      return;
    }
    this.subscribeToLoadingStudentBalance();
  }

  private stopWatchingStudentBalance(): void {
    if (this.loadStudentBalanceSubscription) {
      this.loadStudentBalanceSubscription.unsubscribe();
      this.loadStudentBalanceSubscription = null;
    }
  }

  private async subscribeToLoadingStudentBalance(): Promise<void> {
    // For now, use interval to update student balance from time to time until event from backend is implemented
    const intervalMs: number =
      this.studentStore.watchStudentBalanceIntervalMs$.value || DEFAULT_WATCH_STUDENT_BALANCE_INTERVAL_MS;
    this.loadStudentBalanceSubscription = timer(intervalMs).subscribe(this.handleLoadingStudentBalance.bind(this));
  }

  private async handleLoadingStudentBalance(): Promise<void> {
    await this.studentState.loadStudentBalance();
    if (this.loadStudentBalanceSubscription !== null) {
      this.subscribeToLoadingStudentBalance();
    }
  }

  /**
   * Third Party App Credentials
   */

  private watchThirdPartyAppCredentialsList(): void {
    if (this.loadThirdPartyAppCredentialsListSubscription) {
      return;
    }
    this.subscribeToLoadingThirdPartyAppCredentialsList();
  }

  private stopWatchingThirdPartyAppCredentialsList(): void {
    if (this.loadThirdPartyAppCredentialsListSubscription) {
      this.loadThirdPartyAppCredentialsListSubscription.unsubscribe();
      this.loadThirdPartyAppCredentialsListSubscription = null;
    }
  }

  private async subscribeToLoadingThirdPartyAppCredentialsList(): Promise<void> {
    const intervalMs: number = DEFAULT_WATCH_THIRD_PARTY_APP_CREDENTIALS_INTERVAL_MS;
    this.loadThirdPartyAppCredentialsListSubscription = timer(intervalMs).subscribe(
      this.handleLoadingThirdPartyAppCredentialsList.bind(this)
    );
  }

  private async handleLoadingThirdPartyAppCredentialsList(): Promise<void> {
    await this.studentState.loadThirdPartyAppCredentialsList();
    if (this.loadThirdPartyAppCredentialsListSubscription !== null) {
      this.subscribeToLoadingThirdPartyAppCredentialsList();
    }
  }

  /**
   * Last Learning Activity Time
   */

  private watchLastLearningActivityTime(): void {
    if (this.loadLastLearningActivityTimeSubscription) {
      return;
    }
    this.subscribeToLoadingLastLearningActivityTime();
  }

  private stopWatchingLastLearningActivityTime(): void {
    if (this.loadLastLearningActivityTimeSubscription) {
      this.loadLastLearningActivityTimeSubscription.unsubscribe();
      this.loadLastLearningActivityTimeSubscription = null;
    }
  }

  private async subscribeToLoadingLastLearningActivityTime(): Promise<void> {
    const intervalMs: number = DEFAULT_WATCH_LAST_LEARNING_ACTIVITY_TIME;
    this.loadLastLearningActivityTimeSubscription = timer(intervalMs).subscribe(
      this.handleLoadingLastLearningActivityTime.bind(this)
    );
  }

  private async handleLoadingLastLearningActivityTime(): Promise<void> {
    await this.loadAndCheckLastLearningActivity();
    if (this.loadLastLearningActivityTimeSubscription !== null) {
      this.subscribeToLoadingLastLearningActivityTime();
    }
  }

  /**
   * Tab Events
   */

  private handleTabUpdated(tabId: number, changeInfo: chrome.tabs.TabChangeInfo): void {
    if (changeInfo.url) {
      this.updateCurrentUrl();
    }
  }

  private async loadAndCheckLastLearningActivity(): Promise<void> {
    if (
      this.studentStore.isLearningAnyRecommendation$.value ||
      this.isExtensionPopupOpened$.value ||
      isRestrictedEmail(this.profileEmail$.value)
    ) {
      return;
    }
    await this.studentState.loadLastLearningActivityTime();
    const showLearningReminder: boolean = await this.learningService.checkLastLearningActivity();
    if (showLearningReminder) {
      this.showLearningReminder$.next();
    }
  }
}
